import { useRef, useState } from 'react';

/** Anchor position in viewport coordinates for cursor-based positioning. */
export interface AnchorPosition {
  top: number;
  left: number;
}

export interface UseHoverPopoverOptions {
  /**
   * Whether the hover popover is enabled.
   * When false, the popover will not open on hover.
   * @default true
   */
  enabled?: boolean;
  /**
   * Delay in milliseconds before closing the popover when mouse leaves the trigger.
   * This delay allows users to move their mouse from the trigger to the popover.
   * @default 150
   */
  delay?: number;
  /**
   * How to position the popover.
   * - 'element': anchor to the trigger element (default).
   * - 'cursor': position at the cursor when opening; useful for follow-cursor popups.
   * - 'cursor-x-element-y': X from cursor, Y from trigger element top; e.g. popup follows cursor horizontally but stays n px above the trigger.
   * @default 'element'
   */
  positionMode?: 'element' | 'cursor' | 'cursor-x-element-y';
  /**
   * Offset in px from the cursor when positionMode is 'cursor'.
   * Applied on open; e.g. negative top places the anchor above the cursor.
   * When positionMode is 'cursor-x-element-y', top is offset from trigger top (e.g. -8 = 8px above pill), left is offset from cursor X.
   * @default { top: 0, left: 0 }
   */
  cursorOffset?: { top?: number; left?: number };
}

/**
 * Hook to manage hover popover state and event handlers.
 *
 * This hook provides a complete solution for implementing hover-based popovers that handle
 * smooth mouse movement between the trigger element and the popover without flickering.
 * It's useful for navigation menus, tooltips, preview cards, action menus, and any UI element
 * that needs to show additional content on hover.
 *
 * ## How it works:
 *
 * 1. **State Management**: Maintains the anchor element (the trigger) and popover open state.
 *    The anchor element is used by MUI Popover to position the popover relative to the trigger.
 *
 * 2. **Hover Enter Logic**: When the mouse enters the trigger element:
 *    - Clears any pending close timeout (in case user quickly moves mouse back)
 *    - Immediately sets the anchor element to open the popover
 *    - Only works when `enabled` is true
 *
 * 3. **Hover Leave Logic**: When the mouse leaves the trigger element:
 *    - Sets a configurable delay (default 150ms) before closing the popover
 *    - This delay allows the user to move their mouse from the trigger to the popover
 *    - Without this delay, the popover would close immediately, making it impossible to interact with
 *
 * 4. **Popover Hover Logic**: When hovering over the popover itself:
 *    - Cancels any pending close timeout to keep the popover open
 *    - This ensures the popover stays open while the user interacts with its content
 *
 * 5. **Popover Leave Logic**: When leaving the popover:
 *    - Immediately closes the popover (no delay needed since user is moving away)
 *
 * ## Usage Examples:
 *
 * ### Basic usage (always enabled):
 * ```tsx
 * const {
 *   anchorEl,
 *   menuOpen,
 *   handleMouseEnter,
 *   handleMouseLeave,
 *   handlePopoverMouseEnter,
 *   handlePopoverMouseLeave,
 *   closePopover,
 * } = useHoverPopover();
 *
 * return (
 *   <>
 *     <Button
 *       onMouseEnter={handleMouseEnter}
 *       onMouseLeave={handleMouseLeave}
 *     >
 *       Trigger
 *     </Button>
 *     <Popover
 *       open={menuOpen}
 *       anchorEl={anchorEl}
 *       onMouseEnter={handlePopoverMouseEnter}
 *       onMouseLeave={handlePopoverMouseLeave}
 *     >
 *       Popover Content
 *     </Popover>
 *   </>
 * );
 * ```
 *
 * ### Conditional enabling:
 * ```tsx
 * const { collapsed } = useSidenavContext();
 * const {
 *   anchorEl,
 *   menuOpen,
 *   handleMouseEnter,
 *   handleMouseLeave,
 *   handlePopoverMouseEnter,
 *   handlePopoverMouseLeave,
 * } = useHoverPopover({ enabled: collapsed });
 *
 * return (
 *   <>
 *     <Button
 *       onMouseEnter={handleMouseEnter}
 *       onMouseLeave={handleMouseLeave}
 *     >
 *       Trigger
 *     </Button>
 *     {collapsed && (
 *       <Popover
 *         open={menuOpen}
 *         anchorEl={anchorEl}
 *         onMouseEnter={handlePopoverMouseEnter}
 *         onMouseLeave={handlePopoverMouseLeave}
 *       >
 *         Popover Content
 *       </Popover>
 *     )}
 *   </>
 * );
 * ```
 *
 * ### Custom delay:
 * ```tsx
 * // Use a longer delay for larger popovers that are further from the trigger
 * const { ... } = useHoverPopover({ enabled: true, delay: 300 });
 * ```
 *
 * @param options - Configuration options for the hover popover
 * @param options.enabled - Whether the hover popover is enabled (default: true)
 * @param options.delay - Delay in milliseconds before closing (default: 150)
 * @returns An object containing:
 *   - `anchorEl`: The HTMLElement that serves as the anchor for the popover
 *   - `menuOpen`: Boolean indicating if the popover should be open
 *   - `handleMouseEnter`: Event handler for mouse enter on the trigger element
 *   - `handleMouseLeave`: Event handler for mouse leave on the trigger element
 *   - `handlePopoverMouseEnter`: Event handler for mouse enter on the popover
 *   - `handlePopoverMouseLeave`: Event handler for mouse leave on the popover
 *   - `closePopover`: Function to manually close the popover (useful for click handlers)
 */
export function useHoverPopover(options: UseHoverPopoverOptions | boolean = {}) {
  // Normalize options: support both object and boolean (for backward compatibility)
  const normalizedOptions: UseHoverPopoverOptions =
    typeof options === 'boolean' ? { enabled: options } : { enabled: true, delay: 50, ...options };

  const {
    enabled = true,
    delay = 50,
    positionMode = 'element',
    cursorOffset = {},
  } = normalizedOptions;

  // Stores the DOM element that triggers the popover (used as anchor for positioning in element mode)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Viewport position when positionMode is 'cursor'; used by MUI Popover anchorReference="anchorPosition"
  const [anchorPosition, setAnchorPosition] = useState<AnchorPosition | null>(null);

  // Ref to store the timeout ID for delayed closing
  // Using a ref prevents stale closures and allows clearing the timeout
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearAnchor = () => {
    setAnchorEl(null);
    setAnchorPosition(null);
  };

  /**
   * Handles mouse enter on the trigger element.
   * Opens the popover immediately when enabled, and clears any pending close timeout.
   * In cursor mode stores clientX/clientY (plus offset) for anchorPosition.
   * In cursor-x-element-y mode stores cursor X and trigger element top (plus offset) so popup follows cursor horizontally but Y is fixed relative to the pill.
   * In element mode stores currentTarget as anchorEl.
   */
  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (enabled) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }

      if (positionMode === 'cursor') {
        const top = e.clientY + (cursorOffset.top ?? 0);
        const left = e.clientX + (cursorOffset.left ?? 0);
        setAnchorPosition({ top, left });
      } else if (positionMode === 'cursor-x-element-y') {
        const rect = e.currentTarget.getBoundingClientRect();
        const top = rect.top + (cursorOffset.top ?? 0);
        const left = e.clientX + (cursorOffset.left ?? 0);
        setAnchorPosition({ top, left });
      } else {
        setAnchorEl(e.currentTarget);
      }
    }
  };

  /**
   * Handles mouse leave on the trigger element.
   * Sets a delayed close to allow user to move mouse to the popover.
   */
  const handleMouseLeave = () => {
    if (!enabled) {
      return;
    }

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(clearAnchor, delay);
  };

  /**
   * Handles mouse enter on the popover itself.
   * Cancels any pending close timeout to keep the popover open while hovering.
   */
  const handlePopoverMouseEnter = () => {
    if (!enabled) {
      return;
    }

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  /**
   * Handles mouse leave on the popover.
   * Immediately closes the popover since the user is moving away.
   */
  const handlePopoverMouseLeave = () => {
    clearAnchor();
  };

  /**
   * Manually closes the popover.
   * Useful for programmatic closing (e.g., when a child link is clicked).
   */
  const closePopover = () => {
    clearAnchor();
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const useAnchorPosition = positionMode === 'cursor' || positionMode === 'cursor-x-element-y';
  const menuOpen = useAnchorPosition ? Boolean(anchorPosition) : Boolean(anchorEl);

  const anchorReference: 'anchorEl' | 'anchorPosition' =
    useAnchorPosition && anchorPosition ? 'anchorPosition' : 'anchorEl';

  return {
    anchorEl,
    anchorPosition: anchorPosition ?? undefined,
    anchorReference,
    menuOpen,
    handleMouseEnter,
    handleMouseLeave,
    handlePopoverMouseEnter,
    handlePopoverMouseLeave,
    closePopover,
  };
}
